import React from "react";
import { GitCompare, Loader2, RotateCcw } from "lucide-react";
import { LAYER, num, when } from "../pipeline/Layer";
import { cn } from "../../lib/utils";

export const LAYERS = ["bronze", "silver", "gold"];
export const NAME = { bronze: "Bronze", silver: "Silver", gold: "Gold" };
export const scoreTone = (s) => (s == null ? "text-muted" : s >= 80 ? "text-success" : s >= 60 ? "text-warning" : "text-critical");
// "12,500 rows" (Silver / Gold) or "30,195 of 37,695 rows · 9 files" (Bronze: review samples of big files).
export const rowsText = (t) => `${num(t.rows)}${t.rows_total > t.rows ? ` of ${num(t.rows_total)}` : ""} rows${t.files != null ? ` · ${num(t.files)} file${t.files === 1 ? "" : "s"}` : ""}`;

const ABOUT = {
  bronze: "Every uploaded file except rejected ones, exactly as uploaded — before any cleaning. Files over 10,000 rows count their 10,000-row review sample.",
  silver: "Every promoted table after cleaning and review (certified Gold tables included). The “Use uploaded data” switches use this view.",
  gold: "Only the tables certified Gold.",
};

export function LayerSwitch({ value, onChange }) {
  const opts = [...LAYERS.map(l => [l, NAME[l]]), ["compare", "Compare"]];
  return (
    <div className="inline-flex flex-wrap gap-1 tile p-1" data-testid="rd-layers">
      {opts.map(([id, label]) => (
        <button key={id} data-testid={`rd-layer-${id}`} onClick={() => onChange(id)}
          className={cn("inline-flex items-center gap-2 h-8 px-3.5 rounded-sm text-[12.5px] transition-colors", value === id ? "bg-primary text-primary-fg font-semibold" : "text-muted hover:bg-surface-2 hover:text-fg")}>
          {id === "compare" ? <GitCompare size={13} /> : <span className={cn("w-1.5 h-1.5 rounded-full", LAYER[NAME[id]].dot)} />}{label}
        </button>))}
    </div>
  );
}

// Background calculations (Bronze read, Promote to unlock): progress, the last result while it refreshes, and a retry when it failed.
export function BronzeState({ r, onRetry, what = "Reading Bronze files", testid = "rd-bronze" }) {
  if (!r || r.state === "done") return null;
  if (r.state === "failed") return (
    <div className="text-[12px] text-critical flex flex-wrap items-center gap-2" data-testid={`${testid}-error`}>{r.error}
      <button data-testid={`${testid}-retry`} onClick={onRetry} className="inline-flex items-center gap-1 text-primary hover:underline"><RotateCcw size={12} />Try again</button></div>);
  const pct = r.total ? Math.round((r.done / r.total) * 100) : 0;
  return (
    <div className="space-y-1.5" data-testid={`${testid}-progress`}>
      <div className="text-[12px] text-muted inline-flex items-center gap-1.5"><Loader2 size={12} className="animate-spin" />
        {what} {num(r.done)} of {num(r.total)}{r.stale ? " — showing the last result until it finishes" : ""}</div>
      <div className="h-1.5 bg-surface-2 rounded-full max-w-md"><div className="h-full bg-primary rounded-full transition-[width] duration-500" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function LayerHeader({ layer, r, onRetry }) {
  const c = LAYER[NAME[layer]];
  return (
    <div className={cn("tile p-4 border-l-2 space-y-2", c.border)} data-testid={`rd-head-${layer}`}>
      <div className="flex flex-wrap items-baseline gap-x-3 gap-y-1">
        <span className={cn("text-[10px] uppercase tracking-[0.2em] font-semibold", c.text)}>{NAME[layer]} readiness</span>
        {r?.history_days != null && <span className="text-[11.5px] text-muted font-mono-tabular" data-testid={`rd-days-${layer}`}>{num(r.history_days)} days of sales{layer === "bronze" ? " (from samples)" : ""}</span>}
        {layer === "bronze" && r?.as_of && <span className="text-[11.5px] text-muted font-mono-tabular" data-testid="rd-bronze-asof">{num(r.files)} files · read {when(r.as_of)}</span>}
      </div>
      <p className="text-[12.5px] text-muted max-w-3xl">{ABOUT[layer]}</p>
      {layer === "gold" && r && !r.tables?.length && <p className="text-[12.5px]" data-testid="rd-gold-empty">No table is certified Gold yet — see “Suggested for Gold” below for the tables that can be certified now.</p>}
      {layer === "bronze" && <BronzeState r={r} onRetry={onRetry} />}
      {layer === "bronze" && r?.failed?.length > 0 && (
        <div className="text-[11.5px] text-warning flex flex-wrap items-center gap-2" data-testid="rd-bronze-failed">
          {r.failed.length} file{r.failed.length === 1 ? "" : "s"} couldn't be read and {r.failed.length === 1 ? "isn't" : "aren't"} counted: {r.failed.slice(0, 3).map(f => `${f.filename} (${f.error})`).join("; ")}
          {r.state === "done" && <button data-testid="rd-bronze-reread" onClick={onRetry} className="inline-flex items-center gap-1 text-primary hover:underline"><RotateCcw size={11} />Read again</button>}
        </div>)}
    </div>
  );
}

// Quality score per table in this layer.
export function TableScores({ layer, r }) {
  if (!r?.tables?.length) return null;
  return (
    <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 xl:grid-cols-7 gap-2" data-testid={`rd-tables-${layer}`}>
      {r.tables.map(t => (
        <div key={t.type} className="tile px-3 py-2" data-testid={`rd-table-${layer}-${t.type}`} title={t.findings.map(f => f.title).join("\n")}>
          <div className="text-[11px] text-muted truncate">{t.label}</div>
          <div className="flex flex-wrap items-baseline gap-x-1.5"><span className={cn("font-display text-lg font-semibold font-mono-tabular", scoreTone(t.score))}>{t.score}</span>
            <span className="text-[10.5px] text-muted font-mono-tabular">{rowsText(t)}</span></div>
        </div>))}
    </div>
  );
}
